import React, { useCallback, useState, useEffect } from "react";
import { Button, Collapse, ClickAwayListener, Box } from "@mui/material";

interface IDropdownProps {
  options: IOption[];
  initial: IOption;
  onChange: (value: string) => void;
  minHeight?: string | number;
  id?: string;
}

export interface IOption {
  value: string;
  label: string;
}

interface IClickAway {
  mouseEvent: "onClick" | "onMouseDown" | "onMouseUp" | false;
  touchEvent: "onTouchStart" | "onTouchEnd" | false;
}

const Dropdown: React.FC<IDropdownProps> = props => {
  const { options, initial, onChange, minHeight = 172, id } = props;
  const [expand, setExpand] = useState<boolean>(false);
  const [clickAway, setClickAway] = useState<IClickAway>({
    mouseEvent: false,
    touchEvent: false
  });
  const [selectedOption, setSelectedOption] = useState<IOption>(initial);

  const toggleMenu = useCallback(() => setExpand(!expand), [expand, setExpand]);

  const collapseMenu = useCallback(() => setExpand(false), [setExpand]);

  const selectOption = useCallback(
    (option: IOption) => {
      if (option.value !== selectedOption.value) {
        setSelectedOption(option);
        onChange(option.value);
      }
      setExpand(false);
    },
    [selectedOption, setSelectedOption, onChange]
  );

  useEffect(() => {
    setSelectedOption(initial);
  }, [initial, setSelectedOption]);

  useEffect(
    () =>
      setClickAway({
        mouseEvent: expand && "onClick",
        touchEvent: expand && "onTouchEnd"
      }),
    [expand, setClickAway]
  );

  return (
    <ClickAwayListener {...clickAway} onClickAway={collapseMenu}>
      <Box sx={{ position: "relative" }}>
        <Button
          sx={({ palette, shape }) => ({
            "&.MuiButton-root": {
              zIndex: 2,
              width: "100%",
              minWidth: 156,
              fontSize: "1rem",
              position: "relative",
              lineHeight: "1.375rem",
              color: palette.text.primary,
              padding: "7px 0.5rem 7px 1rem",
              backgroundColor: palette.background.default,
              borderColor: palette.grey[300],
              borderRadius: expand
                ? `${shape.borderRadius}px ${shape.borderRadius}px 0 0`
                : `${shape.borderRadius}px`,
              "&:hover": {
                backgroundColor: palette.background.default
              }
            },
            justifyContent: "space-between",
            textTransform: "none",
            fontWeight: 400
          })}
          variant="outlined"
          onClick={toggleMenu}
          id={id ? id : ""}
        >
          {selectedOption.label}
          <Box
            component={"img"}
            loading='lazy'
            height={24}
            width={24}
            sx={{
              transform: `rotate(${expand ? "180" : "0"}deg)`,
              transition: "transform 200ms"
            }}
            src="https://de-acm-assets.eco.astro.com.my/images/icons/light/caret.svg"
            alt="Expand"
          />
        </Button>
        <Box
          sx={({ palette, shape }) => ({
            position: "absolute",
            top: 29, // NOTE: offset 10px to improve border animation
            zIndex: 1,
            width: "100%",
            backgroundColor: palette.background.paper,
            border: `solid 1px ${palette.grey[400]}`,
            borderBottomLeftRadius: shape.borderRadius,
            borderBottomRightRadius: shape.borderRadius,
            transition: "opacity 200ms ease",
            "& ul": {
              maxHeight: minHeight,
              overflowX: "hidden",
              overflowY: "auto",
              px: 2
            },
            "& li": {
              py: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `solid 1px ${palette.grey[400]}`,
              lineHeight: "1.25",
              transition: "color 100ms",
              cursor: "pointer",
              "&:last-child": {
                borderBottom: "none"
              },
              "&:hover": {
                color: palette.primary.main
              },
              "&.active": {
                color: palette.primary.main
              },
              "& img": {
                maxWidth: "1rem",
                maxHeight: "1rem"
              }
            }
          })}
        >
          <Collapse in={expand} timeout={250}>
            <Box sx={{ mt: 1.25 }} />
            <ul>
              {options.map((option, index) => (
                <li
                  key={index}
                  className={
                    option.value === selectedOption.value ? "active" : ""
                  }
                  onClick={() => selectOption(option)}
                >
                  {option.label}
                  {option.value === selectedOption.value && (
                    <img
                      loading="lazy"
                      width={8}
                      height={5}
                      src="https://de-acm-assets.eco.astro.com.my/images/icons/tick.svg"
                      alt="Selected"
                    />
                  )}
                </li>
              ))}
            </ul>
          </Collapse>
        </Box>
      </Box>
    </ClickAwayListener>
  );
};

Dropdown.defaultProps = {
  options: [],
  onChange: () => undefined
};

export default Dropdown;
